// Administration → Activity (decision 167): who did what, from the audit log every change writes. JL: "i would like to
// see what she did ... a user id when we do place orders or when we do stuff". Owner only. A day (or up to 31) at a
// time, by person and by part of the business; the automatic changes (the QuickBooks copy, nightly jobs) only on ask.
import type { ActivityEntry } from '@morales/shared';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { Empty, ErrorBox, Loading } from '../components/ui';
import { todayIso, whenAt } from '../format';
import { useDesktop, useSearchParam } from '../hooks';
import { useLocale } from '../i18n';
import type { AppMessageKey } from '../messages';

const AREAS = ['sales', 'buying', 'stock', 'prices', 'customers', 'products', 'delivery', 'admin', 'other'] as const;
type Area = (typeof AREAS)[number];

/** The kinds of record people touch most, in words; any other shows its table name. */
const THINGS: Partial<Record<string, AppMessageKey>> = {
  orders: 'actThing_orders', order_lines: 'actThing_order_lines', invoices: 'actThing_invoices', invoice_adjustments: 'actThing_invoice_adjustments', payments: 'actThing_payments',
  credits: 'actThing_credits', purchase_orders: 'actThing_purchase_orders', purchase_order_lines: 'actThing_purchase_order_lines',
  receipts: 'actThing_receipts', inventory_counts: 'actThing_inventory_counts', inventory_movements: 'actThing_inventory_movements',
  price_lists: 'actThing_price_lists', items: 'actThing_items', customers: 'actThing_customers', vendors: 'actThing_vendors',
  vendor_offers: 'actThing_vendor_offers', vendor_items: 'actThing_vendor_items', qbo_links: 'actThing_qbo_links', users: 'actThing_users',
  lost_sales: 'actThing_lost_sales', customer_activities: 'actThing_customer_activities', routes: 'actThing_routes', cost_observations: 'actThing_cost_observations',
};

export function ActivityPage() {
  const { t, intlLocale } = useLocale();
  const desktop = useDesktop();
  const today = todayIso();
  const [fromRaw, setFrom] = useSearchParam('from');
  const [toRaw, setTo] = useSearchParam('to');
  const [user, setUser] = useSearchParam('user');
  const [areaRaw, setArea] = useSearchParam('area');
  const [systemRaw, setSystem] = useSearchParam('system');
  const to = toRaw || today;
  const from = fromRaw || to;
  const area = (AREAS as readonly string[]).includes(areaRaw) ? (areaRaw as Area) : undefined;
  const system = systemRaw === '1';
  const params = { from, to, ...(user ? { user } : {}), ...(area ? { area } : {}), ...(system ? { system: 'true' as const } : {}) };
  const query = useInfiniteQuery({
    queryKey: ['activity', params],
    queryFn: ({ pageParam, signal }) => api.activity({ ...params, ...(pageParam ? { before: pageParam } : {}) }, signal),
    initialPageParam: '',
    getNextPageParam: (last) => last.next ?? undefined,
  });
  const first = query.data?.pages[0];
  const entries = query.data?.pages.flatMap((p) => p.entries) ?? [];
  const thing = (e: ActivityEntry) => (THINGS[e.thing] ? t(THINGS[e.thing]!) : e.thing.replace(/_/g, ' '));
  const what = (e: ActivityEntry) => `${t(`act_${e.action}` as AppMessageKey)} ${thing(e).toLowerCase()}`;
  const label = (e: ActivityEntry) => (
    <>
      {e.link ? <Link to={e.link}>{e.label}</Link> : e.label}
      {e.test ? <span className="badge badge-warn"> {t('actTest')}</span> : null}
    </>
  );
  const changes = (e: ActivityEntry) =>
    e.changes.length ? (
      <span className="row-sub">
        {e.changes.map((c, i) => (
          <span key={i}>
            {i ? ' · ' : ''}
            {c.field}: {c.from ?? '—'} → {c.to ?? '—'}
          </span>
        ))}
      </span>
    ) : null;

  return (
    <div className="desk-page stack" data-testid="activity">
      <header className="desk-head">
        <div>
          <h1>{t('activityTitle')}</h1>
          <p className="muted">{t('activityHint')}</p>
        </div>
      </header>
      <div className="toolbar">
        <label className="tool">
          <span className="label">{t('fromDate')}</span>
          <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="tool">
          <span className="label">{t('toDate')}</span>
          <input type="date" value={to} min={from} max={today} onChange={(e) => setTo(e.target.value)} />
        </label>
        <label className="tool">
          <span className="label">{t('activityPerson')}</span>
          <select value={user} onChange={(e) => setUser(e.target.value)}>
            <option value="">{t('activityEveryone')}</option>
            {user && first && !first.people.some((p) => p.id === user) ? <option value={user}>{t('activityPersonNone')}</option> : null}
            {(first?.people ?? []).filter((p) => p.id).map((p) => (
              <option key={p.id} value={p.id!}>
                {p.name} ({p.count})
              </option>
            ))}
          </select>
        </label>
        <label className="tool">
          <span className="label">{t('activityArea')}</span>
          <select value={area ?? ''} onChange={(e) => setArea(e.target.value)}>
            <option value="">{t('activityAllAreas')}</option>
            {AREAS.map((a) => (
              <option key={a} value={a}>
                {t(`actArea_${a}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="tool">
          <input type="checkbox" checked={system} onChange={(e) => setSystem(e.target.checked ? '1' : '')} /> {t('activitySystem')}
        </label>
      </div>
      {query.isPending ? <Loading /> : null}
      {query.error ? <ErrorBox error={query.error} /> : null}
      {first && entries.length === 0 ? <Empty>{t('activityNone')}</Empty> : null}
      {entries.length > 0 && desktop ? (
        <div className="table-wrap">
          <table className="data-table" aria-label={t('activityTitle')}>
            <thead>
              <tr>
                <th>{t('activityWhen')}</th>
                <th>{t('activityPerson')}</th>
                <th>{t('activityWhat')}</th>
                <th>{t('activityWhich')}</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{whenAt(e.at, intlLocale)}</td>
                  <td>{e.who ?? <span className="muted">{t('activityAutomatic')}</span>}</td>
                  <td>
                    {what(e)}
                    <span className="cell-sub">{t(`actArea_${e.area}`)}</span>
                  </td>
                  <td>
                    {label(e)}
                    {changes(e)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {entries.length > 0 && !desktop ? (
        <ul className="list" aria-label={t('activityTitle')}>
          {entries.map((e) => (
            <li key={e.id} className="row">
              <span className="row-main">
                <span className="row-title">
                  {e.who ?? t('activityAutomatic')} · {what(e)}
                </span>
                <span className="row-sub">{label(e)}</span>
                {changes(e)}
              </span>
              <span className="row-end">
                <span className="row-end-label">{whenAt(e.at, intlLocale)}</span>
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {query.hasNextPage ? (
        <button type="button" className="btn btn-secondary" disabled={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
          {t('activityOlder')}
        </button>
      ) : null}
    </div>
  );
}
